
import { useState } from 'react';
import { loadStripe } from '@stripe/stripe-js';
import {
  Elements,
  CardElement,
  useStripe,
  useElements
} from '@stripe/react-stripe-js';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Loader2, CreditCard } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { supabase } from '@/integrations/supabase/client';

// Load Stripe with your new publishable key
const stripePromise = loadStripe('pk_test_51RYlZhIHpsgW4i7lcy0k7qwc6Vd8otoFs1dhD3jRMTtl7OOzEpsx9BkgJKkjc5zQzRe0WPRXTISQzTs5jMsOts8300Ek5GMfiZ');

const cardElementOptions = {
  style: {
    base: {
      fontSize: '16px',
      color: '#424770',
      '::placeholder': {
        color: '#aab7c4',
      },
    },
    invalid: {
      color: '#9e2146',
    },
  },
};

interface PaymentFormProps {
  amount: number;
  orderData: any;
  onSuccess: (result: any) => void;
  onError: (error: string) => void;
}

const PaymentForm = ({ amount, orderData, onSuccess, onError }: PaymentFormProps) => {
  const stripe = useStripe();
  const elements = useElements();
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [nameOnCard, setNameOnCard] = useState('');

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    
    if (!stripe || !elements) {
      setError('Stripe has not loaded yet. Please try again.');
      return;
    }

    if (!nameOnCard.trim()) {
      setError('Please enter the name on your card.');
      return;
    }

    setIsProcessing(true);
    setError(null);
    
    try {
      // Get the CardElement
      const cardElement = elements.getElement(CardElement);
      
      if (!cardElement) {
        throw new Error('Card element not found');
      }

      // Create payment intent on the server
      const { data: paymentIntentData, error: paymentIntentError } = await supabase.functions.invoke(
        'create-payment-intent',
        {
          body: {
            amount: Math.round(amount * 100), // Convert to cents
            currency: 'usd',
            orderData
          }
        }
      );

      if (paymentIntentError) {
        throw new Error(paymentIntentError.message);
      }

      if (!paymentIntentData.success) {
        throw new Error(paymentIntentData.error || 'Failed to create payment intent');
      }

      // Confirm the payment with the client secret
      const { error: confirmError, paymentIntent } = await stripe.confirmCardPayment(
        paymentIntentData.clientSecret,
        {
          payment_method: {
            card: cardElement,
            billing_details: {
              name: nameOnCard,
              email: orderData.customerEmail,
            },
          },
        }
      );

      if (confirmError) {
        throw new Error(confirmError.message || 'Payment confirmation failed');
      }

      if (paymentIntent?.status === 'succeeded') {
        onSuccess({
          success: true,
          paymentIntentId: paymentIntent.id,
          amount: paymentIntent.amount / 100, // Convert back to dollars
          customerName: orderData.customerName,
          customerEmail: orderData.customerEmail
        });
      } else {
        throw new Error('Payment was not successful');
      }
      
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Payment failed. Please try again.';
      // Payment error handled by toast notification
      setError(errorMessage);
      onError(errorMessage);
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="p-4 border rounded-lg bg-background">
        <div className="space-y-4">
          <div>
            <Label htmlFor="nameOnCard">Name on Card</Label>
            <Input
              id="nameOnCard"
              type="text"
              placeholder="John Doe"
              value={nameOnCard}
              onChange={(e) => setNameOnCard(e.target.value)}
              required
              disabled={isProcessing}
            />
          </div>
          
          <div>
            <Label>Card Information</Label>
            <div className="mt-1 p-3 border rounded-md bg-background">
              <CardElement
                options={cardElementOptions}
              />
            </div>
          </div>
        </div>
      </div>

      {error && (
        <Alert variant="destructive">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      <Button
        type="submit"
        disabled={!stripe || isProcessing}
        className="w-full"
        size="lg"
      >
        {isProcessing ? (
          <>
            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            Processing Payment...
          </>
        ) : (
          <>
            <CreditCard className="h-4 w-4 mr-2" />
            Pay ${amount.toFixed(2)}
          </>
        )}
      </Button>
    </form>
  );
};

interface StripeElementsFormProps {
  amount: number;
  orderData: any;
  onSuccess: (result: any) => void;
  onError: (error: string) => void;
}

const StripeElementsForm = ({ amount, orderData, onSuccess, onError }: StripeElementsFormProps) => {
  return (
    <Elements stripe={stripePromise}>
      <PaymentForm
        amount={amount}
        orderData={orderData}
        onSuccess={onSuccess}
        onError={onError}
      />
    </Elements>
  );
};

export default StripeElementsForm;
